import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'

import {buildIssueNewChooseRoute} from './issue-new-choose-route'
import {IssueNewChoose} from './IssueNewChoose'
import {buildIssuesLayoutRoute} from './issues-layout-route'
import {IssuesLayout} from './IssuesLayout'
import {buildIssueNewRoute} from './issue-new-route'
import {IssueNew} from './IssueNew'
import {buildLabelsRoute} from './labels-route'
import {Labels} from './Labels'
import {labelsLegacyRedirectLoader} from './labels-legacy-redirect-loader'
import {buildMilestoneEditRoute} from './milestone-edit-route'
import {MilestoneEdit} from './MilestoneEdit'
import {buildMilestoneNewRoute} from './milestone-new-route'
import {MilestoneNew} from './MilestoneNew'
import {buildMilestoneIndexRoute} from './milestone-index-route'
import {MilestoneIndex} from './MilestoneIndex'

// The loader always redirects, so nothing is ever shown here. But React Router still
// renders the matched route and warns if a leaf route has no Component. this
// fails the tests (vitest-fail-on-console), so we pass an empty component
const LabelsRedirectFallback = () => null

/**
 * Builds the Issues route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `issues`-bound singletons directly.
 *
 * Reads the `issues_new_react_data_router` client flag, so both call sites (`issues.ts`,
 * `repo.ts`) must invoke it at app registration via the deferred `GetRoutesFunction`,
 * not at module load (unsafe during SSR).
 */
export function createIssuesRoutes(appBuilder: DataRouterApplicationBuilder<string>): RouteObject[] {
  return [
    buildIssuesLayoutRoute(appBuilder).toRoute({
      Component: IssuesLayout,
      children: [
        buildLabelsRoute(appBuilder).toRoute({Component: Labels}),
        // Register issues/new and issues/new/choose only when the flag is on, symmetric with the
        // Relay omission in issues-react/index.ts. With the flag off, the backend serves the Relay
        // `issues-react` bundle, so links hard-navigate to the legacy pages.
        // See github/planning-tracking#3362 and https://github.com/github/issues/issues/22455.
        ...(isFeatureEnabled('issues_new_react_data_router')
          ? [
              buildIssueNewRoute(appBuilder).toRoute({Component: IssueNew}),
              buildIssueNewChooseRoute(appBuilder).toRoute({Component: IssueNewChoose}),
            ]
          : []),
        buildMilestoneNewRoute(appBuilder).toRoute({Component: MilestoneNew}),
        buildMilestoneEditRoute(appBuilder).toRoute({Component: MilestoneEdit}),
        buildMilestoneIndexRoute(appBuilder).toRoute({Component: MilestoneIndex}),
        // defining it as a plain object because its just a legacy-redirect path
        {path: '/:owner/:repo/issues/labels', loader: labelsLegacyRedirectLoader, Component: LabelsRedirectFallback},
      ],
    }),
  ]
}
